import React, { useState, useRef, useEffect } from "react";
import { DayPicker } from "react-day-picker";
import { it } from "date-fns/locale";
import { format } from "date-fns";
import "react-day-picker/dist/style.css";
import { useI18n } from "../i18n";

interface ItalianDatePickerProps {
  selected: Date | null;
  onChange: (date: Date | null) => void;
  minDate?: Date;
  maxDate?: Date;
  placeholderText?: string;
  required?: boolean;
  className?: string;
  availableDates?: string[];  // Array of available date strings in YYYY-MM-DD format
  disabled?: boolean;
  errorMessage?: string;
}

const ItalianDatePicker: React.FC<ItalianDatePickerProps> = ({
  selected,
  onChange,
  minDate,
  maxDate,
  placeholderText,
  required = false,
  className = "form-control",
  availableDates = [],
  disabled = false,
  errorMessage = "",
}) => {
  const { t } = useI18n();
  const ph = placeholderText ?? t("common.selectDate");
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleDaySelect = (date: Date | undefined) => {
    onChange(date || null);
    setIsOpen(false);
  };

  const displayValue = selected ? format(selected, "dd/MM/yyyy") : "";

  // Function to check if a date is in the available dates list
  const isDateDisabled = (date: Date): boolean => {
    // If no available dates specified, use default min/max logic
    if (availableDates.length === 0) {
      return false;
    }
    
    // Check if date is in available dates (comparing date only, not time)
    const dateString = format(date, 'yyyy-MM-dd');
    return !availableDates.includes(dateString);
  };

  // Convert available dates to Date objects for modifier
  const availableDateObjects = availableDates.map(dateStr => {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  });

  // Determine which month to show when calendar opens
  const getDefaultMonth = (): Date => {
    // If a date is selected, show that month
    if (selected) {
      return selected;
    }
    // If there are available dates and a minDate, show the first available date >= minDate
    if (availableDateObjects.length > 0 && minDate) {
      // Normalize minDate to start of day for comparison
      const minDateStart = new Date(minDate);
      minDateStart.setHours(0, 0, 0, 0);
      
      const validDate = availableDateObjects.find(date => {
        const dateStart = new Date(date);
        dateStart.setHours(0, 0, 0, 0);
        return dateStart >= minDateStart;
      });
      
      if (validDate) {
        return validDate;
      }
    }
    // If there are available dates (no minDate constraint), show the first available month
    if (availableDateObjects.length > 0) {
      return availableDateObjects[0];
    }
    // If minDate is in the future, show that month
    if (minDate && minDate > new Date()) {
      return minDate;
    }
    // Otherwise show current month
    return new Date();
  };

  return (
    <>
      <div ref={containerRef} style={{ position: "relative" }}>
        <input
          type="text"
          className={className}
          value={displayValue}
          placeholder={ph}
          required={required}
          readOnly
          disabled={disabled}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          style={{ cursor: disabled ? "not-allowed" : "pointer" }}
        />
        {isOpen && !disabled && (
          <div
            style={{
              position: "absolute",
              zIndex: 1000,
              backgroundColor: "white",
              border: "1px solid #ccc",
              borderRadius: "4px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              marginTop: "4px",
            }}
          >
            <DayPicker
              mode="single"
              selected={selected || undefined}
              onSelect={handleDaySelect}
              locale={it}
              disabled={[
                ...(minDate ? [{ before: minDate }] : []),
                ...(maxDate ? [{ after: maxDate }] : []),
                ...(availableDates.length > 0 ? [(date: Date) => isDateDisabled(date)] : []),
              ]}
              modifiers={availableDates.length > 0 ? { available: availableDateObjects } : {}}
              modifiersStyles={{
                available: {
                  fontWeight: 'bold',
                  color: '#000',
                }
              }}
              defaultMonth={getDefaultMonth()}
              weekStartsOn={1}
              showOutsideDays
              captionLayout="dropdown"
            />
          </div>
        )}
      </div>
      {errorMessage && !disabled && (
        <small className="text-danger d-flex align-items-center mt-2">
          <i className="bi bi-exclamation-triangle-fill me-2"></i>
          {errorMessage}
        </small>
      )}
    </>
  );
};

export default ItalianDatePicker;
